import React, { useState, useEffect, useCallback } from 'react';
import { Package, Shield, BookOpen, Globe, Download, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketingTab({ auth }) {
  const items = [
    {
      key: 'order',
      title: 'Bestill DIY-kit',
      description: 'Tar kunden direkte til bestillingsskjemaet. Perfekt for salgsflyer og esker.',
      path: '/bestill-kit',
      label: 'bestill-kit',
      heading: 'Bestill ditt DIY-kit',
      subheading: 'Skann QR-koden med mobilkameraet',
      icon: <Package size={18} className="text-emerald-700" />,
    },
    {
      key: 'landing',
      title: 'DIY-landingsside',
      description: 'Markedsføringssiden med utfyllende informasjon før bestilling. Bruk på plakater og brosjyrer.',
      path: '/diy-kit',
      label: 'diy-landing',
      heading: 'Lær mer om DIY-kittet',
      subheading: 'Skann QR-koden for å lese mer',
      icon: <BookOpen size={18} className="text-emerald-700" />,
    },
    {
      key: 'home',
      title: 'Forsiden',
      description: 'Lenker til hovedsiden gravsteinoppfriskeren.no. Egnet for visittkort og generelt materiell.',
      path: '/',
      label: 'forside',
      heading: 'Gravsteinoppfriskeren.no',
      subheading: 'Skann for å besøke nettsiden',
      icon: <Globe size={18} className="text-emerald-700" />,
    },
    {
      key: 'warranty',
      title: 'Registrer garanti',
      description: 'Tar kunden direkte til garantiregistreringen. Bruk på kit-etiketter eller informasjonskort.',
      path: '/registrer-garanti',
      label: 'registrer-garanti',
      heading: 'Registrer din 10 års garanti',
      subheading: 'Skann QR-koden etter påføring',
      icon: <Shield size={18} className="text-emerald-700" />,
    },
  ];

  return (
    <div data-testid="marketing-tab">
      <h2 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-2">Markedsføringsmateriell</h2>
      <p className="text-stone-600 text-sm mb-6">Last ned eller skriv ut QR-koder som tar kunden rett til ulike sider på nettstedet. Bruk dem på flyer, plakater, esker og digitalt materiell.</p>

      <div className="grid lg:grid-cols-2 gap-5">
        {items.map(item => (
          <MarketingQRCard key={item.key} item={item} auth={auth} />
        ))}
      </div>

      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-stone-700 max-w-3xl">
        <strong className="block mb-1">Tips for trykk:</strong>
        Hver PNG er <strong>1125×1125 piksler</strong> — skarp på opptil ca. 9 cm i 300 dpi. Bruk minst 3×3 cm størrelse på flyer for god skanning, og legg gjerne en kort tekst som <em>«Skann og bestill»</em> ved siden av QR-koden.
      </div>
    </div>
  );
}

function MarketingQRCard({ item, auth }) {
  const orderUrl = `${window.location.origin}${item.path}`;
  const qrSrc = `${API}/admin/marketing/diy-qr?size=25&path=${encodeURIComponent(item.path)}&label=${encodeURIComponent(item.label)}`;
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  // Fetch the QR with auth so the <img> never hits a 401 first.
  useEffect(() => {
    let revokeUrl = '';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(qrSrc, { headers: { 'Authorization': `Basic ${auth}` } });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        const obj = URL.createObjectURL(blob);
        revokeUrl = obj;
        if (!cancelled) setPreviewUrl(obj);
      } catch { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [qrSrc, auth]);

  const downloadQr = async () => {
    try {
      const res = await fetch(`${qrSrc}&download=true`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.label}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const printQr = async () => {
    try {
      const res = await fetch(qrSrc, { headers: { 'Authorization': `Basic ${auth}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html><html><head><title>QR — ${item.title}</title>
          <style>
            @page { size: A4; margin: 20mm; }
            body { font-family: Arial, sans-serif; text-align: center; padding: 0; margin: 0; }
            .wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 90vh; }
            img { width: 120mm; height: 120mm; }
            h1 { font-family: Georgia, serif; font-size: 32px; color: #064e3b; margin: 8mm 0 4mm; }
            p { font-size: 16px; color: #44403c; margin: 2mm 0; }
            .url { font-family: monospace; font-size: 14px; color: #78716c; margin-top: 6mm; }
            @media print { .no-print { display: none; } }
          </style></head><body>
          <button class="no-print" onclick="window.print()" style="position:fixed;top:8px;right:8px;padding:8px 18px;background:#064e3b;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer;">Skriv ut</button>
          <div class="wrap">
            <h1>${item.heading}</h1>
            <p>${item.subheading}</p>
            <img src="${dataUrl}" alt="QR" />
            <p class="url">${orderUrl}</p>
          </div>
        </body></html>`);
        win.document.close();
      };
      reader.readAsDataURL(blob);
    } catch { /* ignore */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(orderUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-white rounded-xl shadow border border-stone-200 p-5" data-testid={`marketing-card-${item.key}`}>
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="flex-shrink-0 mx-auto sm:mx-0">
          <div className="bg-white border-2 border-stone-200 rounded-xl p-2" data-testid={`marketing-qr-preview-${item.key}`}>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={`QR-kode: ${item.title}`}
                className="w-40 h-40 object-contain"
                style={{ imageRendering: 'pixelated' }}
              />
            ) : (
              <div className="w-40 h-40 flex items-center justify-center text-stone-400 text-sm">Laster QR...</div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2">{item.icon} {item.title}</h3>
          <p className="text-sm text-stone-600 mt-1">{item.description}</p>

          <div className="bg-stone-50 rounded-lg p-2 mt-3 text-xs">
            <div className="flex items-center gap-2">
              <code className="font-mono text-stone-700 break-all flex-1" data-testid={`marketing-qr-url-${item.key}`}>{orderUrl}</code>
              <button onClick={copy} className="text-emerald-800 hover:text-emerald-900 whitespace-nowrap font-medium" data-testid={`marketing-copy-url-btn-${item.key}`}>
                {copied ? 'Kopiert!' : 'Kopier'}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={downloadQr} className="flex items-center gap-1.5 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-3 py-2 text-sm font-medium" data-testid={`marketing-download-qr-btn-${item.key}`}>
              <Download size={16} /> Last ned PNG
            </button>
            <button onClick={printQr} className="flex items-center gap-1.5 bg-stone-900 text-white hover:bg-stone-800 rounded-lg px-3 py-2 text-sm font-medium" data-testid={`marketing-print-qr-btn-${item.key}`}>
              <Printer size={16} /> Skriv ut A4
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// =====================  Contact requests inbox  =====================
